<template>
   <div
      class="fixed left-0 z-100 size-full max-h-24 border-b-2 border-grey-10 bg-white transition-colors print:relative"
      data-cy="top-nav"
      :style="`top: ${isAccountRepBannerVisible ? '2.625rem' : '0px'}`"
   >
      <nav class="container flex h-24 w-full items-center justify-between">
         <div class="relative z-10 mr-auto flex h-24 w-full items-center justify-between md:w-auto">
            <!-- Navigation logo -->
            <NavLogoLink data-cy="nav-logo" />

            <!-- Hamburger (mobile only)-->
            <SiteNavHamburger
               data-cy="mobile-menu-hamburger"
               :mobileMenuIsShowing="state.mobileMenuIsShowing"
               @toggle-mobile-menu="toggleMobileMenu"
            />
         </div>
         <div
            class="absolute z-10 -ml-4 bg-grey-10 md:hidden"
            :class="{ hidden: !state.mobileMenuIsShowing }"
            style="width: calc(100% + 2rem); top: calc(6rem - 2px); height: 2px"
         ></div>

         <!-- Desktop menu Or Mobile menu sidebar - navigation items -->
         <transition name="mobile-nav-fade">
            <div
               v-show="state.mobileMenuIsShowing"
               class="navbar"
               :class="[
                  { 'no-transitions': state.preventTransitions },
                  { 'pt-28 md:pt-0': !userStore.isImpersonating && !userStore.isAccountRepresented },
                  { 'pt-[9.625rem] md:pt-0': isAccountRepBannerVisible },
               ]"
            >
               <!-- Spinner (mobile only) -->
               <div v-if="state.isProcessing" class="absolute flex w-full justify-center pt-32 md:hidden">
                  <LoadingSpinner color="default" size="md" />
               </div>
               <div
                  class="flex h-full flex-col md:h-auto md:flex-row md:items-center md:justify-between main-links"
                  :class="{ 'opacity-0': state.isProcessing }"
               >
                  <!-- Custom navbar links -->
                  <LinkButton
                     v-for="link in customLinks"
                     :key="`link-to-${link.url}`"
                     :url="link.url"
                     class="navlink"
                     style-type="ghost"
                     size="sm"
                     :isExternal="link.isExternal"
                     :target="link.target"
                     :color="Theme.Black"
                  >
                     {{ link.text }}
                  </LinkButton>

                  <!-- Default Logged-in Links: -->
                  <template v-if="authStore.authTokenExists">
                     <!-- Account -->
                     <LinkButton
                        data-test="account-btn"
                        data-cy="sitenav-account-link"
                        url="/account"
                        class="navlink"
                        size="sm"
                        style-type="ghost"
                        :color="Theme.Black"
                     >
                        {{ $t("authentication.account") }}
                     </LinkButton>
                     <!-- Logout -->
                     <DefaultButton
                        data-test="log-out-btn"
                        data-cy="sitenav-logout-btn"
                        class="navlink text-left"
                        size="sm"
                        style-type="ghost"
                        :color="Theme.Black"
                        @click="logout"
                     >
                        {{ $t("authentication.logout") }}
                     </DefaultButton>
                  </template>

                  <!-- Default Logged-out Links: -->
                  <template v-else>
                     <!-- Login -->
                     <LinkButton
                        data-test="log-in-btn"
                        url="/login"
                        class="navlink"
                        data-cy="sitenav-login-link"
                        style-type="ghost"
                        size="sm"
                        :color="Theme.Black"
                     >
                        {{ $t("authentication.login") }}
                     </LinkButton>
                     <!-- Signup -->
                     <LinkButton
                        data-test="sign-up-btn"
                        v-if="clientSettings.signup.isActive"
                        data-cy="sitenav-register-link"
                        :url="signupUrl"
                        class="navlink"
                        style-type="filled"
                        size="sm"
                     >
                        {{ $t("authentication.signup") }}
                     </LinkButton>
                  </template>

                  <!-- both Logged-in and Logged-out -->
               </div>
            </div>
         </transition>
         <!-- Language choice -->
         <SiteNavLocaleSelect
            v-if="showLocaleSelect"
            :key="localeStore.currentLocale?.id"
            @close-mobile-menu="closeMenu"
         />
         <!-- Print page -->
         <button
            v-if="enablePrintPage"
            type="button"
            class="print-page-btn"
            aria-label="Print page"
            title="Print page"
            data-test="print-page-btn"
            @click="openPrintDialogue()"
         >
            <SnIcon name="printer" size="medium" aria-hidden="true" />
         </button>
      </nav>
   </div>
</template>

<script setup lang="ts">
import { useRouteLocale } from "@/composables/useRouteLocale";
import { onBeforeRouteUpdate } from "vue-router";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useLocaleStore } from "@/stores/localeStore";
import { useUserStore } from "@/stores/userStore";
import { useBaseContentStore } from "@/stores/baseContentStore";
import { useAuthStore } from "@/stores/authStore";
import { useSettingsStore } from "@/stores/settingsStore.ts";
import { Theme } from "@/types";
import { useRouter } from "vue-router";
import { SnIcon } from "@sano-ui-library/src";

const router = useRouter();
const localeStore = useLocaleStore();
const clientSettings = inject(useClientSettingsKey);
const userStore = useUserStore();
const baseContentStore = useBaseContentStore();
const authStore = useAuthStore();
const { localePath } = useRouteLocale();
const { setLazyLoad } = useSettingsStore();
const customSignupRedirect = inject<Ref<string | null>>("customSignupRedirect");

interface State {
   mobileMenuIsShowing: boolean;
   preventTransitions: boolean;
   resizeTimeout: null | ReturnType<typeof setTimeout>;
   isProcessing: boolean;
   windowWidth: number;
}

const state = reactive<State>({
   mobileMenuIsShowing: false,
   preventTransitions: false,
   resizeTimeout: null,
   // Use isProcessing to show a spinner on the mobile menu only when logging out / doing something without an immediate route change
   isProcessing: false,
   windowWidth: 0,
});

// -----------------------------------------
onBeforeRouteUpdate((to, from) => {
   // On route change, close the mobile menu
   state.mobileMenuIsShowing = false;
   state.isProcessing = false;
});

// computed
// -----------------------------------------
const sitenav = computed(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.siteNav;
});

const customLinks = computed(() => {
   if (!sitenav?.value?.navItems) return [];
   return sitenav.value.navItems.filter(
      (item) =>
         (authStore.authTokenExists && item.hideOn !== "logged_in") ||
         (!authStore.authTokenExists && item.hideOn !== "logged_out")
   );
});

// is the account representation banner showing, if so, we need to make some CSS changes
const isAccountRepBannerVisible = computed(() => {
   return userStore.isImpersonating || userStore.isAccountRepresented;
});

const showLocaleSelect = computed(() => localeStore.siteSupportedLocales.length > 1 && localeStore.currentLocale);

const signupUrl = computed(() => {
   if (!customSignupRedirect.value) return "/signup";
   return `/signup?redirect=${customSignupRedirect.value}`;
});

// methods
// -----------------------------------------
function toggleMobileMenu() {
   state.mobileMenuIsShowing = !state.mobileMenuIsShowing;
}

function closeMenu() {
   state.preventTransitions = true;
   state.mobileMenuIsShowing = false;

   // If the user is resizing the window, turn off our transitions for a reasonable length of time
   // to prevent them seeing any flashes of mobile menu
   clearTimeout(state.resizeTimeout);
   state.resizeTimeout = setTimeout(() => {
      if (state.preventTransitions) state.preventTransitions = false;
   }, 500);
}

async function logout() {
   // Show the spinner here because we need to wait for the logout process before the route will change and menu will close
   // but closing the menu immediately also seems a bit odd in this case and doesn't indicate that logout is happening
   state.isProcessing = true;
   await userStore.logout();
   // force reload update so as to clear all app data
   window.location.href = `${localePath.value}/`;
}

const enablePrintPage = computed(() => {
   return router.currentRoute.value.name === "blogpost";
});

async function openPrintDialogue(): Promise<void> {
   // disable lazy load images so they are printed
   await setLazyLoad(false);
   // small delay to allow images to load
   setTimeout(() => {
      window.print();
      // Re-enable lazy loading after print dialogue is closed
      setLazyLoad(true);
   }, 300);
}

// lifecycle methods
// -----------------------------------------
onMounted(() => {
   window.addEventListener("resize", closeMenu);
});

onUnmounted(() => {
   window.removeEventListener("resize", closeMenu);
});
</script>

<style scoped lang="scss">
.navlink {
   @apply mr-4 block;

   @media (max-width: 47.9375em) {
      @apply px-5 py-2 mx-2;
      color: var(--colour-brand-default) !important;
      width: calc(100% - 1rem);
      background-color: transparent !important;
      border: none !important;
      text-align: left;
      font-size: 1.875rem !important; /* 30px / part of 3xl*/
      line-height: 2.25rem !important; /* 36px / part of 3xl */

      /* remove on-hover effect on mobile (nb: '!'' in tailwind + scss doesn't work!) */
      &:hover {
         background-color: transparent !important;
         color: var(--colour-brand-default) !important;
      }
   }
}

.main-links {
   transition: opacity 0.15s ease-in-out;
}

.print-page-btn {
   color: var(--black);
   aspect-ratio: 1/1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: var(--size-base-6);
   width: var(--size-base-36);
   height: var(--size-base-36);
   margin-left: var(--size-base-8);
   &:hover {
      background-color: var(--grey-10);
   }
}

@media (max-width: 47.9375em) {
   .navbar {
      @apply bg-white fixed left-0 top-0 w-full pb-3;
      height: 100dvh;
   }

   .navbar.no-transitions {
      transition-duration: 0s !important;
   }
}

@media print {
   .print-page-btn {
      display: none;
   }
}

@media (min-width: 48em) {
   .navbar {
      display: block !important;
   }
   .print-page-btn {
      width: var(--size-base-40);
      height: var(--size-base-40);
   }
}

.mobile-nav-fade-enter-active,
.mobile-nav-fade-leave-active {
   transition: opacity 0.3s ease-in-out;
}
.mobile-nav-fade-enter-from,
.mobile-nav-fade-leave-to {
   /* transform: translateX(-100%); */
   opacity: 0;
}
</style>
